/**
 * <ingredients-carousel> — the "Five actives" card row.
 *
 * A Swiper at every breakpoint, but `watchOverflow` means it only behaves as one when
 * the cards actually overflow: at desktop widths all five fit, Swiper locks itself, and
 * the controls are hidden. On narrower screens the next card peeks in to signal that the
 * row swipes.
 *
 * Controls come from snippets/carousel-nav.liquid and are styled to match Figma's
 * Bullet Navigation.
 */

// Wrapped so nothing here reaches global scope: these are classic scripts, and a
// top-level name shared with another file makes the whole file throw before it can
// register its element. Also what makes a duplicate <script> tag for this file safe.
(() => {
  class IngredientsCarousel extends HTMLElement {
    connectedCallback() {
      if (typeof window.Swiper === 'undefined') {
        window.addEventListener('load', () => this.init(), { once: true });
        return;
      }

      this.init();
    }

    disconnectedCallback() {
      this.swiper?.destroy(true, true);
    }

    init() {
      if (typeof window.Swiper === 'undefined') return;

      const container = this.querySelector('[data-carousel]');
      if (!container) return;

      this.nav = this.querySelector('.carousel-nav');

      this.swiper = new window.Swiper(container, {
        // 'auto' takes the card width from CSS (192.8px, per Figma) so the next card
        // peeks in; from 1024 the five are sized to the row instead.
        slidesPerView: 'auto',
        spaceBetween: 16,
        watchOverflow: true,
        // Tapping the peeking card slides to it rather than doing nothing.
        slideToClickedSlide: true,
        breakpoints: {
          1024: { slidesPerView: 5, spaceBetween: 16 },
        },
        navigation: {
          prevEl: this.querySelector('[data-carousel-prev]'),
          nextEl: this.querySelector('[data-carousel-next]'),
        },
        pagination: {
          el: this.querySelector('[data-carousel-pagination]'),
          clickable: true,
          bulletClass: 'carousel-nav__bullet',
          bulletActiveClass: 'carousel-nav__bullet--active',
        },
        keyboard: { enabled: true, onlyInViewport: true },
      });

      const syncNav = () => {
        if (this.nav) this.nav.hidden = this.swiper.isLocked;
      };

      this.swiper.on('lock', syncNav);
      this.swiper.on('unlock', syncNav);
      syncNav();
    }
  }

  if (!customElements.get('ingredients-carousel')) {
    customElements.define('ingredients-carousel', IngredientsCarousel);
  }
})();
